import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import { Add, Delete, Edit, HomeWork } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';

const PROPERTY_TYPES = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'land', label: 'Land' },
  { value: 'condo', label: 'Condo' },
  { value: 'other', label: 'Other' },
] as const;

const TRANSACTION_TYPES = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'sale', label: 'Sale' },
  { value: 'refinance', label: 'Refinance' },
  { value: 'lease', label: 'Lease' },
  { value: 'other', label: 'Other' },
] as const;

export interface CasePropertyRecord {
  id: string;
  address?: string | null;
  parcelId?: string | null;
  propertyType?: string | null;
  transactionType?: string | null;
  closingDate?: string | null;
  purchasePrice?: number | string | null;
  titleNotes?: string | null;
  knownIssues?: string | null;
  sortOrder?: number;
}

interface CasePropertiesPanelProps {
  caseId: string;
}

const emptyForm = () => ({
  address: '',
  parcelId: '',
  propertyType: '',
  transactionType: '',
  closingDate: '',
  purchasePrice: '',
  titleNotes: '',
  knownIssues: '',
});

const labelFor = (
  options: ReadonlyArray<{ value: string; label: string }>,
  value?: string | null
) => options.find((o) => o.value === value)?.label || value || '—';

const formatPrice = (value?: number | string | null) => {
  if (value == null || value === '') return '—';
  const num = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(num);
};

const CasePropertiesPanel: React.FC<CasePropertiesPanelProps> = ({ caseId }) => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CasePropertyRecord | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState<CasePropertyRecord | null>(null);

  const { data: properties = [], isLoading, error } = useQuery({
    queryKey: ['case-properties', caseId],
    enabled: Boolean(caseId),
    queryFn: async (): Promise<CasePropertyRecord[]> => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');
      const res = await fetch(`/api/case-intake/${caseId}/properties`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Unable to load case properties.'));
      }
      const json = await res.json();
      return Array.isArray(json.data) ? json.data : [];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      const payload = {
        address: form.address.trim() || null,
        parcelId: form.parcelId.trim() || null,
        propertyType: form.propertyType || null,
        transactionType: form.transactionType || null,
        closingDate: form.closingDate || null,
        purchasePrice: form.purchasePrice === '' ? null : Number(form.purchasePrice),
        titleNotes: form.titleNotes.trim() || null,
        knownIssues: form.knownIssues.trim() || null,
      };

      const url = editing
        ? `/api/case-intake/${caseId}/properties/${editing.id}`
        : `/api/case-intake/${caseId}/properties`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(
          await getApiErrorMessage(res, editing ? 'Unable to update property.' : 'Unable to add property.')
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-properties', caseId] });
      toast.success(editing ? 'Property updated' : 'Property added');
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm());
    },
    onError: (err) => {
      toast.error(toUserErrorMessage(err, 'Unable to save property.'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (propertyId: string) => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');
      const res = await fetch(`/api/case-intake/${caseId}/properties/${propertyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Unable to delete property.'));
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-properties', caseId] });
      toast.success('Property deleted');
      setDeleteTarget(null);
    },
    onError: (err) => {
      toast.error(toUserErrorMessage(err, 'Unable to delete property.'));
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (property: CasePropertyRecord) => {
    setEditing(property);
    setForm({
      address: property.address || '',
      parcelId: property.parcelId || '',
      propertyType: property.propertyType || '',
      transactionType: property.transactionType || '',
      closingDate: property.closingDate ? String(property.closingDate).slice(0, 10) : '',
      purchasePrice:
        property.purchasePrice == null || property.purchasePrice === ''
          ? ''
          : String(property.purchasePrice),
      titleNotes: property.titleNotes || '',
      knownIssues: property.knownIssues || '',
    });
    setDialogOpen(true);
  };

  const canSave = useMemo(
    () => Boolean(form.address.trim() || form.parcelId.trim()),
    [form.address, form.parcelId]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    console.error('Case properties load error:', error);
    return (
      <Alert severity="error">
        Unable to load case properties. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Case properties</Typography>
          <Typography variant="body2" color="text.secondary">
            Parcel and closing records used for real estate matters and template merge fields.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Add property
        </Button>
      </Box>

      {properties.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <HomeWork sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No properties on this case
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add a parcel or closing record so merge fields like RE_PropertyAddress can populate.
            </Typography>
            <Button variant="outlined" startIcon={<Add />} onClick={openCreate}>
              Add property
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Address / Parcel</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Transaction</TableCell>
                <TableCell>Closing</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.map((property) => (
                <TableRow key={property.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {property.address || 'Untitled property'}
                    </Typography>
                    {property.parcelId && (
                      <Typography variant="caption" color="text.secondary">
                        Parcel: {property.parcelId}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={labelFor(PROPERTY_TYPES, property.propertyType)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{labelFor(TRANSACTION_TYPES, property.transactionType)}</TableCell>
                  <TableCell>{property.closingDate || '—'}</TableCell>
                  <TableCell align="right">{formatPrice(property.purchasePrice)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(property)} aria-label="Edit property">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(property)}
                      aria-label="Delete property"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit property' : 'Add property'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Property address"
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Parcel ID"
                value={form.parcelId}
                onChange={(e) => setForm((p) => ({ ...p, parcelId: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Closing date"
                InputLabelProps={{ shrink: true }}
                value={form.closingDate}
                onChange={(e) => setForm((p) => ({ ...p, closingDate: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Property type</InputLabel>
                <Select
                  label="Property type"
                  value={form.propertyType}
                  onChange={(e) => setForm((p) => ({ ...p, propertyType: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {PROPERTY_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Transaction</InputLabel>
                <Select
                  label="Transaction"
                  value={form.transactionType}
                  onChange={(e) => setForm((p) => ({ ...p, transactionType: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {TRANSACTION_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Purchase / sale price"
                value={form.purchasePrice}
                onChange={(e) => setForm((p) => ({ ...p, purchasePrice: e.target.value }))}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Title notes"
                value={form.titleNotes}
                onChange={(e) => setForm((p) => ({ ...p, titleNotes: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Known issues"
                value={form.knownIssues}
                onChange={(e) => setForm((p) => ({ ...p, knownIssues: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!canSave || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add property'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete property?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes the property record from the case. Template merge will no longer use it
            until you add another property.
          </Typography>
          {deleteTarget?.address && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              {deleteTarget.address}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CasePropertiesPanel;
